/* Kneetoes quick-add "Select Size" drawer.
 *
 * - Hover CTA (.js-kt-qa-open) opens the per-card <kt-quick-add> drawer.
 * - Size dropdown expands/collapses; picking a size sets the hidden variant id,
 *   updates the toggle label, highlights the choice, and enables ADD TO CART.
 * - ADD TO CART submits the existing Prestige <product-form> (default flow),
 *   so the cart behaves exactly like the theme's normal add-to-cart.
 */
(function () {
  'use strict';

  // Register the element so it isn't treated as an unknown inline element
  // (unknown elements default to display:inline, which collapses the fixed
  // drawer to its content size and centers it).
  if (window.customElements && !window.customElements.get('kt-quick-add')) {
    window.customElements.define('kt-quick-add', class extends HTMLElement {});
  }

  var openDrawer = null;

  function open(drawer) {
    if (openDrawer) close();
    openDrawer = drawer;
    // The drawer is authored inside the product card. A transformed/contained
    // ancestor (card hover effects, lazyload, etc.) becomes the containing
    // block for position:fixed, which traps the drawer inside the card box
    // instead of the viewport. Re-parent it to <body> so it covers the screen.
    if (drawer.parentNode !== document.body) {
      drawer._ktQaHome = drawer._ktQaHome || drawer.parentNode;
      document.body.appendChild(drawer);
    }
    drawer.hidden = false;
    // next frame so the transition runs
    requestAnimationFrame(function () { drawer.classList.add('is-open'); });
    document.documentElement.classList.add('kt-qa-open');
  }

  // `afterAdd` marks the close that follows a successful add-to-cart. In that
  // case Prestige is about to (or already has) wiped and rebuilt the
  // cart-drawer DOM: _ktQaHome now points at a detached orphan, and the
  // re-rendered drawer already contains a fresh hidden copy of this node. So we
  // close instantly (no slide-out, which would overlap the cart re-render and
  // flicker) and DISCARD the body-level orphan instead of re-homing it into a
  // dead subtree or duplicating its id.
  //
  // A plain close (X / overlay / ESC, no add) keeps the slide-out and re-homes
  // the node so it can be reopened.
  function close(afterAdd) {
    if (!openDrawer) return;
    var d = openDrawer;
    openDrawer = null;
    d.classList.remove('is-open');
    document.documentElement.classList.remove('kt-qa-open');

    var discardOrphan = afterAdd &&
      d.classList.contains('kt-qa-drawer--cart') &&
      d.parentNode === document.body;

    function finish() {
      d.hidden = true;
      if (discardOrphan) {
        // Authored copy lives inside the (re-rendered) cart drawer.
        if (d.parentNode) d.parentNode.removeChild(d);
        return;
      }
      // Put the drawer back where it was authored so card-scoped selectors
      // and re-opens keep working.
      if (d._ktQaHome && d.parentNode !== d._ktQaHome) {
        d._ktQaHome.appendChild(d);
      }
    }

    if (afterAdd) { finish(); } else { setTimeout(finish, 300); }
  }

  document.addEventListener('click', function (e) {
    // Open
    var opener = e.target.closest('.js-kt-qa-open');
    if (opener) {
      e.preventDefault();
      var drawer = document.getElementById(opener.getAttribute('data-kt-qa-target'));
      if (drawer) open(drawer);
      return;
    }

    // Close
    if (e.target.closest('.js-kt-qa-close')) { close(); return; }

    if (!openDrawer) return;

    // Toggle size dropdown
    var toggle = e.target.closest('.js-kt-qa-size-toggle');
    if (toggle) {
      var list = openDrawer.querySelector('.js-kt-qa-size-list');
      var expanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!expanded));
      if (list) list.hidden = expanded;
      return;
    }

    // Pick a size
    var option = e.target.closest('.js-kt-qa-size-option');
    if (option && !option.disabled) {
      var drawerEl = option.closest('.kt-qa-drawer');
      // highlight
      drawerEl.querySelectorAll('.js-kt-qa-size-option').forEach(function (o) {
        o.classList.remove('kt-qa-size__option--selected');
      });
      option.classList.add('kt-qa-size__option--selected');
      // set variant id
      var hidden = drawerEl.querySelector('.js-kt-qa-variant-id');
      if (hidden) hidden.value = option.getAttribute('data-variant-id');
      // update label
      var label = drawerEl.querySelector('.js-kt-qa-size-label');
      if (label) label.textContent = option.getAttribute('data-variant-title');
      // collapse list
      var tgl = drawerEl.querySelector('.js-kt-qa-size-toggle');
      var lst = drawerEl.querySelector('.js-kt-qa-size-list');
      if (tgl) tgl.setAttribute('aria-expanded', 'false');
      if (lst) lst.hidden = true;
      // enable ATC
      var atc = drawerEl.querySelector('.js-kt-qa-atc');
      if (atc) atc.disabled = false;
      return;
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && openDrawer) close();
  });

  // After the product-form successfully adds, close the size drawer so the
  // cart (theme/Shopflo default flow) takes over cleanly. Close IMMEDIATELY
  // (no slide-out) because Prestige's <cart-drawer> wipes and rebuilds its DOM
  // on these events — a delayed close would overlap the cart re-render/re-open
  // and flicker, and would try to re-home the drawer into a detached node.
  ['cart:refresh', 'cart:change', 'cart:add', 'on:cart:add'].forEach(function (evt) {
    document.addEventListener(evt, function () { if (openDrawer) close(true); });
  });
})();

/* ── Mobile size picker (.msp-row) — ported from US ──────────────────────────
 * Inline size <select> + cart button on mobile. Picking a size enables the
 * button; clicking it adds the variant via /cart/add.js (cart-nudge + Prestige
 * both hook /cart/add, so the cart updates normally).
 */
(function () {
  'use strict';
  if (window.__mspInit) return;
  window.__mspInit = true;

  function syncButton(row) {
    var sel = row.querySelector('.js-msp-select');
    var btn = row.querySelector('.js-msp-cart');
    if (sel && btn) btn.disabled = !sel.value;
  }

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function addToCart(btn, sel) {
    btn.classList.add('msp-atc--loading');
    btn.disabled = true;

    // Ask the cart-drawer (and any other listeners) which sections need
    // re-rendering, so the drawer can refresh its own markup after the add —
    // same handshake Prestige's <product-form> uses.
    var sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
      bubbles: true,
      detail: { sections: sectionsToBundle }
    }));

    var body = new FormData();
    body.append('id', parseInt(sel.value, 10));
    body.append('quantity', 1);
    body.append('sections', sectionsToBundle.join(','));

    fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      body: body
    }).then(function (res) {
      return res.json().then(function (json) { return { ok: res.ok, json: json }; });
    }).then(function (result) {
      btn.classList.remove('msp-atc--loading');
      sel.value = '';
      btn.disabled = true;

      if (!result.ok) {
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        return;
      }

      var added = result.json;
      return fetch(root + 'cart.js').then(function (r) { return r.json(); }).then(function (cart) {
        cart.sections = added.sections;
        // Fire the same event Prestige's product-form fires on success. The
        // cart-drawer listens for `cart:change` with baseEvent "variant:add"
        // + onSuccessDo "force_open_drawer" and calls show().
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'variant:add',
            onSuccessDo: 'force_open_drawer',
            cart: cart
          }
        }));
        document.dispatchEvent(new CustomEvent('ajaxProduct:added'));
      });
    }).catch(function () {
      btn.classList.remove('msp-atc--loading');
      btn.disabled = false;
    });
  }

  // ── Delegated listeners on document ──────────────────────────────────────
  // Bound once, so they keep working for cards inserted later by AJAX facet
  // filtering / pagination (which replace section content WITHOUT firing
  // shopify:section:load). Per-card binding missed those cards entirely.

  // Enable/disable the cart button whenever a size is chosen.
  document.addEventListener('change', function (evt) {
    var sel = evt.target.closest && evt.target.closest('.js-msp-select');
    if (!sel) return;
    var row = sel.closest('.msp-row');
    if (row) syncButton(row);
  });

  // Add to cart when the (enabled) cart button is clicked.
  document.addEventListener('click', function (evt) {
    var btn = evt.target.closest && evt.target.closest('.js-msp-cart');
    if (!btn) return;
    evt.preventDefault();
    evt.stopPropagation();
    if (btn.disabled) return;
    var row = btn.closest('.msp-row');
    var sel = row && row.querySelector('.js-msp-select');
    if (!sel || !sel.value) return;
    addToCart(btn, sel);
  });

  // Stop the select from triggering card navigation / hover handlers.
  document.addEventListener('mousedown', function (evt) {
    if (evt.target.closest && evt.target.closest('.js-msp-select')) evt.stopPropagation();
  });

  // On initial load (and re-render), reflect any pre-selected value.
  function syncAll() {
    document.querySelectorAll('.msp-row').forEach(syncButton);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncAll);
  } else {
    syncAll();
  }
  document.addEventListener('shopify:section:load', syncAll);
})();
